import { formatBreakEven, formatMoney } from "@shared/roi/format";
import type { Projection, ScenarioModel } from "@shared/roi/types";
import { EmptyState } from "@client/components/EmptyState";
import { Panel } from "@client/components/Panel";
import { CumulativeChart } from "./CumulativeChart";
import { ProjectionTable } from "./ProjectionTable";
import { StackedBreakdownChart } from "./StackedBreakdownChart";
import { YearlyFlowsChart } from "./YearlyFlowsChart";

/**
 * The editor's main view: three charts and the table, all drawing the same
 * `Projection` the KPI strip summarises, at the full width of the page now that
 * the inputs live behind the rail.
 *
 * Nothing here fetches or computes anything. The projection arrives already
 * computed from the draft, so every figure on screen — tile, bar, line, cell —
 * is the same `useMemo` result and they cannot disagree with one another.
 */
export function OutputsColumn({
  model,
  projection,
  currencyCode,
}: {
  model: ScenarioModel;
  projection: Projection;
  currencyCode: string;
}) {
  const { rows } = projection;

  if (rows.length === 0) {
    return (
      <Panel title="Nothing to project">
        <EmptyState
          title="This scenario has no years"
          description="A projection needs at least one year in its horizon. Set the horizon in scenario settings — the first button on the rail — and the charts appear."
        />
      </Panel>
    );
  }

  const breakEven = formatBreakEven(projection.breakEvenYears, rows.length);

  return (
    <>
      <Panel
        title="Revenue against cost"
        infoLabel="this chart"
        info="Each year on its own, on one money axis. The gap between the columns is that year's net."
      >
        <YearlyFlowsChart rows={rows} currencyCode={currencyCode} />
      </Panel>

      <Panel
        title="Cumulative position"
        description={`Payback — ${breakEven.toLowerCase()}.`}
        infoLabel="this chart"
        info="Everything earned against everything spent, running. Where the lines cross is payback."
      >
        <CumulativeChart
          rows={rows}
          breakEvenYears={projection.breakEvenYears}
          currencyCode={currencyCode}
        />
      </Panel>

      <Panel
        title="Where the money goes"
        infoLabel="this chart"
        info="Revenue split by package, cost split by line-item group. A cost line on the wrong basis shows up here first."
      >
        <StackedBreakdownChart model={model} rows={rows} currencyCode={currencyCode} />
      </Panel>

      <Panel
        title="Year by year"
        description={`${formatMoney(currencyCode, projection.cumRevenue, {
          compact: true,
        })} earned, ${formatMoney(currencyCode, projection.cumCost, {
          compact: true,
        })} spent across the horizon.`}
        infoLabel="this table"
        info="Every figure the charts draw. Expand a year for the split by package. This is what the Export button writes out."
      >
        <ProjectionTable model={model} rows={rows} currencyCode={currencyCode} />
      </Panel>
    </>
  );
}
